// @flow

import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { __ } from '../../../i18n/l10n';
import Inputmask from 'inputmask';
import { colorPalette, colors, sizes, fontFamily } from '../../../styles/GlobalStyle';
import { Icon } from './Icon';

const EditPictoWrapper = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
  color: ${colors.yellow};
  outline: 0;
  cursor: pointer;
  svg {
    width: 18px;
    height: 18px;
  }
`;

const LabelWithIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const LeftIconImg = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

type InputTextProps = {|
  className?: string,
  value: string,
  name: string,
  label: string,
  id: string,
  invalid?: boolean,
  type?: string,
  customAttributes?: any,
  onChange?: (value: string) => any,
  onActiveChange?: (value: boolean) => void,
  onFocusField?: (value: boolean) => void,
  onBlurField?: () => void,
  onKeyDownField?: (value: any) => void,
  onClickField?: () => void,
  optional?: boolean,
  hasClearButton?: boolean,
  mask?: Object,
  readOnlyField?: boolean,
  onClearInput?: () => void,
  labelOnTop?: boolean,
  displayLockedIcon?: boolean,
  leftIcon?: string,
  scale?: boolean,
|};

const InputTextRaw = forwardRef<InputTextProps, HTMLInputElement>(
  (
    {
      className = null,
      value,
      name,
      label,
      id,
      invalid = false,
      type = 'text',
      onChange,
      onActiveChange,
      onFocusField,
      onBlurField,
      onKeyDownField,
      onClickField,
      customAttributes = null,
      optional = false,
      hasClearButton = false,
      mask = null,
      readOnlyField = false,
      editable = false,
      onClickEdit,
      onClearInput,
      labelOnTop = false,
      displayLockedIcon = true,
      hidden = false,
      leftIcon = null,
      scale = false,
    },
    ref
  ) => {
    const [sValue, setValue] = useState<string>(value);
    const [isMasked, setMasked] = useState(false);
    const [active, setActive] = useState<boolean>(
      typeof value !== 'undefined' && value !== null && value !== ''
    );
    const [valuePreviouslyReceived, setValuePreviouslyReceived] = useState(false);

    const innerRef = useRef(null);

    useImperativeHandle(ref, () => innerRef.current);

    const onAnimationStart = useCallback((e: AnimationEvent) => {
      if (e.animationName === 'onAutoFillStart') {
        setActive(true);
      }
    }, []);

    useEffect(() => {
      const input = innerRef.current;
      if (input) {
        // Remove autocomplete to fix overlapping with label
        input.setAttribute('autocomplete', 'new-password');
        input.addEventListener('animationstart', onAnimationStart);

        return () => {
          input.removeEventListener('animationstart', onAnimationStart);
        };
      }

      return () => {};
    }, [onAnimationStart]);

    useEffect(() => {
      let valueToSet = value;
      if (type === 'tel' && typeof valueToSet === 'string' && valueToSet) {
        const pointIndex = valueToSet.indexOf('.');
        if (pointIndex !== -1) {
          valueToSet = valueToSet.substr(pointIndex);
        }
      }
      if (valueToSet || valueToSet === '') setValue(valueToSet);

      if (innerRef.current && typeof valueToSet !== 'undefined') {
        innerRef.current.value = valueToSet;
      }
      setValuePreviouslyReceived(true);
    }, [type, value]);

    useEffect(() => {
      if (onActiveChange) {
        onActiveChange(
          active || !!sValue || (mask !== null && mask.suffix && sValue === mask.suffix)
        );
      }
      if (onFocusField) onFocusField(active);
    }, [onActiveChange, active, sValue, onFocusField, mask]);

    useEffect(() => {
      if (mask && !isMasked) {
        setMasked(true);
        const maskOptions = { ...mask, placeholder: '' };
        if (maskOptions.extension) delete maskOptions.extension;
        if (mask.extension) {
          Inputmask(mask.extension, maskOptions).mask(innerRef.current);
        } else {
          Inputmask(maskOptions).mask(innerRef.current);
        }
      }
    }, [isMasked, mask, value]);

    const preventEnter = useCallback((event) => {
      if ((event.charCode || event.keyCode) === 13) {
        event.preventDefault();
        // We don't want to submit the form when the user presses enter on a field with autocomplete
        if (
          event.target.name === 'birth_city' ||
          event.target.name === 'zip_code' ||
          event.target.name === 'city' ||
          event.target.name === 'address1'
        ) {
          event.stopPropagation();
        }
      }
    }, []);

    const updateValue = useCallback(
      (newValue) => {
        if (onChange && (sValue !== newValue || valuePreviouslyReceived)) {
          onChange(newValue);
        }
        setValue(newValue);
        setValuePreviouslyReceived(false);
      },
      [onChange, sValue, valuePreviouslyReceived]
    );

    const clearInput = useCallback(() => {
      const input = innerRef.current;
      if (input) {
        input.value = '';
        updateValue('');
        input.focus();
        onClearInput && onClearInput();
      }
    }, [updateValue, onClearInput]);

    const focusTimeout = useRef();

    const handleFocus = useCallback(
      (event) => {
        // https://stackoverflow.com/a/61973547
        event.target.setAttribute('autocomplete', 'new-password');
        if (focusTimeout.current) clearTimeout(focusTimeout.current);
        setActive(true);
        if (mask && mask.suffix) {
          const input = event.target;
          let selectionIndex = input.value.length - mask.suffix.length;
          if (selectionIndex < 0) selectionIndex = 0;
          // Fix for inputmask lib bad focus with suffix
          setTimeout(() => {
            input.setSelectionRange(selectionIndex, selectionIndex);
          }, 100);
        }

        return () => {
          if (focusTimeout.current) clearTimeout(focusTimeout.current);
        };
      },
      [mask]
    );

    const handleBlur = useCallback(() => {
      if (onBlurField) {
        onBlurField();
      }
      setActive(false);
    }, [onBlurField]);

    return (
      <span
        className={`${className || ''}${
          active ||
          ((sValue || value === 0) && (!mask || !mask.suffix || sValue !== mask.suffix)) ||
          labelOnTop
            ? ' active'
            : ''
        }${invalid ? ' invalid' : ''}${readOnlyField ? ' locked-span' : ''}${
          hidden ? ' hidden-field' : ''
        }`}
      >
       <label className="label" htmlFor={id}>
          {leftIcon ? (
            <LabelWithIconWrapper>
              <LeftIconImg src={leftIcon} alt="" />
              <span>
                {label}
                {optional && <span className="label-optional">{__('Optional')}</span>}
              </span>
            </LabelWithIconWrapper>
          ) : (
            <>
              {label}
              {optional && <span className="label-optional">{__('Optional')}</span>}
            </>
          )}
        </label>
        <input
          className={readOnlyField ? "locked-input" : "input"}
          type={type}
          id={id}
          name={name}
          defaultValue={sValue}
          ref={innerRef}
          placeholder=""
          onKeyUp={(event) => {
            updateValue(event.target.value);
          }}
          onChange={(event) => {
            updateValue(event.target.value);
          }}
          onKeyDown={(event) => {
            if (onKeyDownField) {
              onKeyDownField(event);
            }
            preventEnter(event);
          }}
          onClick={onClickField}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoCorrect="off"
          spellCheck="false"
          autoCapitalize="off"
          // TODO: change this for specific props
          {...customAttributes}
          readOnly={readOnlyField}
        />
        {hasClearButton && sValue && sValue.length !== 0 && (
          <div
            className="input-clear"
            onClick={clearInput}
            role="button"
            tabIndex={-1}
            onKeyUp={() => {}}
          >
            <Icon name="close" />
          </div>
        )}
        {readOnlyField && displayLockedIcon && (
          <div className="input-read-only" tabIndex={-1}>
            <Icon name="lock" />
          </div>
        )}
        {readOnlyField && editable && (
          <EditPictoWrapper onClick={onClickEdit}>
            <Icon name="edit" />
          </EditPictoWrapper>
        )}
      </span>
    );
  }
);

InputTextRaw.displayName = 'InputText';

export const InputText: StyledComponent<InputTextProps, {}, typeof InputTextRaw> = styled(
  InputTextRaw
)`
  position: relative;
  display: inline-block;
  width: 100%;
  border-radius: ${sizes.borderRadius};
  padding-top: 20px;
  font-weight: ${fontFamily.primary.black.fontWeight};

  &:before {
    display: block;
    content: '';
    position: absolute;
    bottom: 5px;
    height: 2px;
    background: ${colorPalette.primary.active};
    width: 100%;
    z-index: 2;
  }

  &:after {
    display: block;
    content: '';
    position: absolute;
    bottom: 5px;
    height: 2px;
    z-index: 1;
    background: ${(props) => (props.readOnlyField ? colors.grey : '#fff')};
    width: 0;
    transition: all 0.6s;
    opacity: 0;
    z-index: 2;
  }

  .label {
    position: absolute;
    top: 40px;
    display: block;
    width: 100%;
    text-transform: uppercase;
    transform-origin: top left;
    transition: all 200ms ease-out;
    z-index: 2;
  }

  @media (max-width: 375px) {
    .label {
      top: 30px;
    }
  }

  .label-optional {
    display: inline-block;
    padding-left: ${sizes.padding};
    font-weight: ${fontFamily.primary.bold.fontWeight};
    text-transform: none;
  }

  &.hidden-field {
    display: none;
  }

  .locked-input {
    position: relative;
    width: 100%;
    height: 60px;
    background: transparent !important;
    border: none;
    // border-bottom: 2px solid ${colorPalette.primary.active};
    font-weight: ${fontFamily.primary.black.fontWeight};
    color: ${colors.grey} !important;
    caret-color: ${colors.white};
    outline: none;

    &[type='number'] {
      -moz-appearance: textfield;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &:-webkit-autofill {
      border: none;
      -webkit-text-fill-color: ${colors.grey};
      -webkit-box-shadow: 0 0 0px 1000px ${colors.black} inset;
      animation-name: onAutoFillStart;
      animation-delay: 100ms;
      transition: background-color 50000s ease-in-out 0s;
    }
  }

  .input {
    position: relative;
    width: 100%;
    height: 60px;
    background: transparent !important;
    border: none;
    // border-bottom: 2px solid ${colorPalette.primary.active};
    font-weight: ${fontFamily.primary.black.fontWeight};
    color: ${colors.white} !important;
    caret-color: ${colors.white};
    outline: none;

    &[type='number'] {
      -moz-appearance: textfield;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }

    &:-webkit-autofill {
      border: none;
      -webkit-text-fill-color: ${colors.white};
      -webkit-box-shadow: 0 0 0px 1000px ${colors.black} inset;
      animation-name: onAutoFillStart;
      animation-delay: 100ms;
      transition: background-color 50000s ease-in-out 0s;
    }

    &:not(:-webkit-autofill) {
      animation-name: onAutoFillCancel;
    }

    &::-ms-clear {
      display: none;
    }

    &:invalid {
      box-shadow: none;
    }
  }

  &.active {
    .label {
      transform: ${(props) => (props.leftIcon || props.scale ? 'translateY(-20px) scale(0.8)' : 'translateY(-20px) scale(0.625)')};
      color: ${colorPalette.primary.active};
    }
    &:after {
      width: 100%;
      opacity: 1;
    }
  }

  &.invalid {
    .label {
      color: ${colorPalette.danger.active};
    }

    &:before {
      background-color: ${colorPalette.danger.active};
    }
  }

  .input-clear {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    cursor: pointer;
    outline: 0;
    transition: 300ms all ease-out;
    animation: scaleIn 0.3s ease-out;
    svg {
      width: 18px;
      height: 18px;
    }
    &:hover {
      color: #fff;
    }
  }

  .input-read-only {
    position: absolute;
    right: 0;
    bottom: 20px;
    color: ${colors.yellow};
    outline: 0;
    svg {
      width: 18px;
      height: 18px;
    }
  }

  @keyframes scaleIn {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;
